import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { AcDialog, Button, ChipPicker, DragHandle, Field, Icon, IconButton, Spinner, Textarea, useDropTarget, DialogCancel } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useBandTeam } from "../../app/hooks/useBandTeam.js";
import { roleLabelFrom, useSeatRoles } from "../../app/hooks/useSeatRoles.js";
import { orgInitials } from "../../app/hooks/useOrg.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import {
  toPersonPickerChip,
  toPersonPickerOption,
  type PersonPickerRow,
} from "../shared/PersonPickerOption.js";
import {
  useReleaseTemplate,
  useResetReleaseTemplate,
  useSaveReleaseTemplate,
  type ReleaseTemplateRowDto,
} from "../../app/hooks/useReleaseTemplate.js";
import { Modal } from "./Modal.js";
import { SpotifyCatalogImportModal } from "./SpotifyCatalogImportModal.js";
import styles from "./ReleaseSettingsModal.module.scss";

/**
 * Release settings — the band's ONE to-do template, plus the Spotify importer that used to sit in the hub
 * header. Two sections, not the spec's three: Writers is deferred (TASK-067).
 *
 * The template is what every new release is STAMPED with, so everything here is about future releases —
 * editing a row never reaches back into a release that already exists. The one exception is deliberate and
 * lives in the backend: moving a release's date re-computes the due dates of the tasks its template
 * produced, unless someone has re-dated them by hand.
 *
 * The whole template saves in one PUT guarded by If-Match, so the editor holds a LOCAL DRAFT until Save.
 * Rows keep their `rowId` across the round-trip — that identity is what keeps already-stamped tasks pointed
 * at the row that made them.
 *
 * The list is COLLAPSED by default and one row expands at a time (the spec's "Edit opens the full preset
 * editor"). Four fields per row × six rows is a wall of inputs you cannot scan; the summary line is what
 * someone opening this actually came to read.
 */
export interface ReleaseSettingsModalProps {
  onClose: () => void;
}

/** The editor's row: the DTO plus a client-side key, since a brand-new row has no server id yet. */
interface DraftRow {
  key: string;
  rowId: string | null;
  label: string;
  daysBefore: string;      // a STRING while editing: "" is a real state ("no due date"), and 0 is not it
  notes: string;
  assigneePersonIds: string[];
  /** Read-only provenance — one of the four we ship. Never sent back; the server owns it. */
  isDefault: boolean;
}

let draftSeq = 0;
const newKey = () => `draft-${++draftSeq}`;

function toDraft(row: ReleaseTemplateRowDto): DraftRow {
  return {
    key: (row.rowId as string) ?? newKey(),
    rowId: (row.rowId as string) ?? null,
    label: row.label ?? "",
    daysBefore: row.daysBefore == null ? "" : String(row.daysBefore),
    notes: row.notes ?? "",
    assigneePersonIds: (row.assigneePersonIds ?? []) as string[],
    isDefault: row.isDefault ?? false,
  };
}

/** The collapsed row's timing line. Signed: a negative offset lands AFTER the release. */
function timingLabel(daysBefore: string): string {
  if (daysBefore === "") return "No due date";
  const n = Number(daysBefore);
  if (!Number.isInteger(n)) return "—";
  if (n === 0) return "Release day";
  return n > 0 ? `${n} days before` : `${Math.abs(n)} days after`;
}

export function ReleaseSettingsModal({ onClose }: ReleaseSettingsModalProps) {
  const canWrite = useCapability("releases:write");
  const template = useReleaseTemplate();
  const save = useSaveReleaseTemplate();
  const reset = useResetReleaseTemplate();
  const team = useBandTeam();
  const seatRoles = useSeatRoles();

  const [rows, setRows] = useState<DraftRow[] | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const version = template.data?.version ?? 0;

  // Re-sync the draft from the server whenever the version advances — the same eviction contract the other
  // draft editors use, so a save (or a reset, or someone else's save on refetch) never leaves a stale draft
  // on screen. Collapse the open editor too: the row it was pointing at may no longer exist.
  useEffect(() => {
    if (template.data) {
      setRows(template.data.rows.map(toDraft));
      setEditingKey(null);
    }
  }, [template.data]);

  // The band's members, ready for the shared person picker (ART-289): face, name and the job resolved from
  // the TITLE (never the role), plus the role key for the AV mark. The OWNER is left out — "the artist IS the
  // band", so its row reads as the band's own name, not a person to hand a to-do to (the task drawer's rule).
  // Kept in `byId` regardless, so a template row an owner was already on still renders its face.
  const { people, byId } = useMemo(() => {
    const all: (PersonPickerRow & { isOwner: boolean })[] = (team.data?.items ?? []).flatMap((s) =>
      s.personId
        ? [
            {
              personId: s.personId,
              displayName: s.displayName || "Member",
              avatarMediaId: s.avatarMediaId ?? null,
              jobLabel: roleLabelFrom(seatRoles.data?.bandRoles, s.titleKey),
              roleKey: s.role ?? null,
              isOwner: s.isOwner ?? false,
            },
          ]
        : [],
    );
    return {
      people: all.filter((p) => !p.isOwner),
      byId: new Map<string, PersonPickerRow>(all.map((p) => [p.personId, p])),
    };
  }, [team.data, seatRoles.data]);

  function patch(key: string, change: Partial<DraftRow>) {
    setRows((prev) => (prev ?? []).map((r) => (r.key === key ? { ...r, ...change } : r)));
  }

  function move(from: number, to: number) {
    setRows((prev) => {
      const next = [...(prev ?? [])];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  function addRow() {
    const key = newKey();
    setRows((prev) => [
      ...(prev ?? []),
      { key, rowId: null, label: "", daysBefore: "", notes: "", assigneePersonIds: [], isDefault: false },
    ]);
    setEditingKey(key);   // a new row opens OPEN — it has nothing to summarise yet
  }

  function submit() {
    setError(null);
    const draft = rows ?? [];

    // A row with no title is the one thing the server silently drops, so say so here instead of letting
    // someone watch a row vanish on save. Open the offender so the message has somewhere to point.
    const blank = draft.find((r) => r.label.trim().length === 0);
    if (blank) {
      setEditingKey(blank.key);
      setError("Every to-do needs a name — remove the blank one or give it a title.");
      return;
    }
    const bad = draft.find((r) => r.daysBefore !== "" && !Number.isInteger(Number(r.daysBefore)));
    if (bad) {
      setEditingKey(bad.key);
      setError(`"${bad.label}" needs a whole number of days, or none at all.`);
      return;
    }

    save.mutate(
      {
        version,
        rows: draft.map((r) => ({
          rowId: r.rowId,
          label: r.label.trim(),
          daysBefore: r.daysBefore === "" ? null : Number(r.daysBefore),
          notes: r.notes.trim() === "" ? null : r.notes.trim(),
          assigneePersonIds: r.assigneePersonIds,
        })),
      },
      { onSuccess: () => onClose(), onError: (err) => setError(err.message) },
    );
  }

  const loading = template.isLoading || rows === null;
  const busy = save.isPending || reset.isPending;

  return (
    <Modal
      dirty="auto"
      title="Release settings"
      onClose={onClose}
      size="lg"
      fitViewport
    >
      <div className={styles.wrap}>
        <div className={styles.scroll}>
          <section className={styles.section}>
            <h3 className={styles.subhead}>To-do template</h3>
            <p className={styles.sectionNote}>
              Each new release is stamped with these, due the given number of days before the release date.
            </p>

            {loading ? (
              <div className={styles.center}>
                <Spinner size={18} label="Loading the template…" />
              </div>
            ) : (
              <>
                <ul className={styles.rows}>
                  {rows.map((row, i) => (
                    <TemplateRow
                      key={row.key}
                      row={row}
                      index={i}
                      count={rows.length}
                      canWrite={canWrite}
                      editing={editingKey === row.key}
                      onToggleEdit={() => setEditingKey(editingKey === row.key ? null : row.key)}
                      people={people}
                      byId={byId}
                      teamLoading={team.isLoading}
                      onChange={(change) => patch(row.key, change)}
                      onRemove={() => {
                        setRows((prev) => (prev ?? []).filter((r) => r.key !== row.key));
                        if (editingKey === row.key) setEditingKey(null);
                      }}
                      onMove={move}
                    />
                  ))}
                </ul>

                {rows.length === 0 && (
                  <p className={styles.empty}>
                    No to-dos in the template. New releases will start with an empty checklist.
                  </p>
                )}

                {canWrite && (
                  <div className={styles.listActions}>
                    <Button variant="ghost" size="sm" onClick={addRow}>
                      <Icon icon={Plus} size={16} /> Add to-do
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmReset(true)}
                      disabled={busy}
                    >
                      <Icon icon={RotateCcw} size={16} /> Reset template
                    </Button>
                  </div>
                )}
              </>
            )}
          </section>

          <section className={styles.section}>
            <h3 className={styles.subhead}>Import</h3>
            <div className={styles.importRow}>
              <div>
                <p className={styles.importTitle}>Import previous releases from Spotify</p>
                <p className={styles.importNote}>
                  Your released catalogue, as Spotify has it. Opens the picker — choose what to bring in.
                </p>
              </div>
              {canWrite && (
                <Button variant="ghost" onClick={() => setImportOpen(true)}>
                  Import from Spotify
                </Button>
              )}
            </div>
          </section>
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <DialogCancel />
          {canWrite && (
            <Button onClick={submit} loading={save.isPending} disabled={loading || reset.isPending}>
              Save settings
            </Button>
          )}
        </div>
      </div>

      {importOpen && <SpotifyCatalogImportModal onClose={() => setImportOpen(false)} />}

      {confirmReset && (
        <AcDialog
          // Title is the ACTION, because the message asks the question — the two restating each other
          // ("Reset the to-do template?" / "Are you sure you want to reset the to-do template…") reads as a
          // stutter in a two-line dialog.
          title="Reset template"
          message={
            "Are you sure you want to reset the to-do template back to the default? This action is " +
            "irreversible, but will not affect releases that have already been created."
          }
          confirmLabel="Reset template"
          cancelLabel="Keep mine"
          tone="danger"
          confirmIcon={RotateCcw}
          loading={reset.isPending}
          onCancel={() => setConfirmReset(false)}
          onConfirm={() =>
            reset.mutate(
              { version },
              {
                onSuccess: () => {
                  setConfirmReset(false);
                  setError(null);
                },
                onError: (err) => {
                  setConfirmReset(false);
                  setError(err.message);
                },
              },
            )
          }
        />
      )}
    </Modal>
  );
}

function TemplateRow({
  row,
  index,
  count,
  canWrite,
  editing,
  onToggleEdit,
  people,
  byId,
  teamLoading,
  onChange,
  onRemove,
  onMove,
}: {
  row: DraftRow;
  index: number;
  count: number;
  canWrite: boolean;
  editing: boolean;
  onToggleEdit: () => void;
  people: PersonPickerRow[];
  byId: Map<string, PersonPickerRow>;
  teamLoading: boolean;
  onChange: (change: Partial<DraftRow>) => void;
  onRemove: () => void;
  onMove: (from: number, to: number) => void;
}) {
  const drop = useDropTarget({ index, kind: "release-template-row", onMove });
  const name = row.label.trim() || "Untitled to-do";
  // An assignee no longer on the band still has to render as SOMETHING — a nameless initials chip.
  const personOf = (id: string): PersonPickerRow =>
    byId.get(id) ?? { personId: id, displayName: "Member", avatarMediaId: null };

  return (
    <li className={styles.row} data-editing={editing || undefined} {...drop}>
      <div className={styles.summary}>
        {canWrite && (
          <DragHandle label={name} index={index} count={count} kind="release-template-row" onMove={onMove} />
        )}

        <div className={styles.summaryText}>
          <span className={styles.summaryLine}>
            <span className={styles.summaryName} data-untitled={row.label.trim() === "" || undefined}>
              {name}
            </span>
            {/* Provenance, not a state the band controls — so it is a flat tag, not a toggle. It survives an
                edit to the row, because the row still came from us. */}
            {row.isDefault && <span className={styles.defaultTag}>Default</span>}
          </span>
          <span className={styles.summaryMeta}>{timingLabel(row.daysBefore)}</span>
        </div>

        {row.assigneePersonIds.length > 0 && (
          <div className={styles.avatars}>
            {row.assigneePersonIds.slice(0, 3).map((id) => {
              const p = personOf(id);
              return (
                <PersonAvatar
                  key={id}
                  personId={p.personId}
                  avatarMediaId={p.avatarMediaId}
                  initials={orgInitials(p.displayName)}
                  size="sm"
                />
              );
            })}
            {row.assigneePersonIds.length > 3 && (
              <span className={styles.avatarMore}>+{row.assigneePersonIds.length - 3}</span>
            )}
          </div>
        )}

        {canWrite && (
          <>
            <Button variant="ghost" size="sm" onClick={onToggleEdit} aria-expanded={editing}>
              <Icon icon={Pencil} size={16} /> {editing ? "Done" : "Edit"}
            </Button>
            <IconButton icon={Trash2} label={`Remove ${name}`} onClick={onRemove} />
          </>
        )}
      </div>

      {editing && (
        <div className={styles.editor}>
          <div className={styles.editorHead}>
            <Field
              label="To-do"
              placeholder="e.g. Album Art"
              value={row.label}
              disabled={!canWrite}
              onChange={(e) => onChange({ label: e.target.value })}
            />
            <Field
              label="Days before release"
              type="number"
              placeholder="—"
              value={row.daysBefore}
              disabled={!canWrite}
              hint="Negative lands after the release. Blank means no due date."
              onChange={(e) => onChange({ daysBefore: e.target.value })}
              className={styles.daysField}
            />
          </div>

          <ChipPicker
            label="Assigned to"
            options={people
              .filter((p) => !row.assigneePersonIds.includes(p.personId))
              .map(toPersonPickerOption)}
            chips={row.assigneePersonIds.map((id) => toPersonPickerChip(personOf(id)))}
            onAdd={(value) => onChange({ assigneePersonIds: [...row.assigneePersonIds, value] })}
            onRemove={(value) =>
              onChange({ assigneePersonIds: row.assigneePersonIds.filter((id) => id !== value) })
            }
            loading={teamLoading}
            loadingLabel="Loading the band…"
            placeholder="Anyone can pick it up"
          />

          <Textarea
            label="Default notes"
            placeholder="Rides into the task when it's stamped on a new release."
            value={row.notes}
            disabled={!canWrite}
            rows={2}
            onChange={(e) => onChange({ notes: e.target.value })}
          />
        </div>
      )}
    </li>
  );
}
